import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../design/Icon';
import { Avatar } from './Avatar';
import { GlobalSearch } from './GlobalSearch';
import { useAppTo } from './links';
import { HOME, PRIMARY_SERVICES, isActivePath, isAvailable, type ServiceDef } from './services';
import { useShell } from './ShellContext';
import { SpaceSwitcher } from './SpaceSwitcher';
import { ThemeToggle } from './ThemeToggle';
import type { SidebarMode } from './useSidebarState';

const SOON = 'Bientôt disponible';

/** Item de la sidebar dépliée (44 px, libellé) ou de la languette (48 px, icône seule + aria-label et title). */
function NavItem({ service, pathname, rail }: { service: ServiceDef; pathname: string; rail: boolean }) {
  const { session } = useShell();
  const to = useAppTo();
  const active = isActivePath(pathname, service.path);
  const Badge = service.badge;
  const className = 'sh-item';
  const content = (
    <>
      <Icon name={service.icon} size={rail ? 24 : 20} />
      {rail ? null : (
        <>
          <span className="sh-item__label">{service.label}</span>
          {Badge ? (
            <Suspense fallback={null}>
              <Badge session={session} />
            </Suspense>
          ) : null}
        </>
      )}
    </>
  );

  if (!isAvailable(service)) {
    return (
      <button type="button" className={className} data-rail={rail} aria-disabled="true" aria-label={rail ? service.label : undefined} title={rail ? `${service.label} · ${SOON}` : SOON}>
        {content}
      </button>
    );
  }
  return (
    <Link
      to={to(service.path!)}
      className={className}
      data-rail={rail}
      data-active={active}
      aria-current={active ? 'page' : undefined}
      aria-label={rail ? service.label : undefined}
      title={rail ? service.label : undefined}
    >
      {content}
    </Link>
  );
}

function AddItem({ rail }: { rail: boolean }) {
  return (
    <button type="button" className="sh-item sh-item--add" data-rail={rail} aria-disabled="true" aria-label="Ajouter un service" title={`Ajouter un service · ${SOON}`}>
      <Icon name="plus" size={rail ? 24 : 20} />
      {rail ? null : <span className="sh-item__label">Ajouter</span>}
    </button>
  );
}

/**
 * Sidebar globale : dépliée (260 px, libellés) ou languette (76 px, icônes). La poignée collée au bord la
 * réduit / la déplie ; la tuile d'espace de la languette la déplie aussi.
 */
export function GlobalSidebar({ mode, onToggle, pathname }: { mode: SidebarMode; onToggle: () => void; pathname: string }) {
  const { user, space } = useShell();
  const rail = mode === 'collapsed';
  const handleLabel = rail ? 'Agrandir le menu' : 'Réduire le menu';

  return (
    <aside className="sh-sb" data-mode={mode} aria-label="Menu principal">
      <button type="button" className="sh-sb__handle" aria-label={handleLabel} title={handleLabel} aria-expanded={!rail} onClick={onToggle}>
        <Icon name={rail ? 'right' : 'chevleft'} size={14} />
      </button>

      {rail ? (
        <button type="button" className="sh-tile" aria-label={`${space.label}, agrandir le menu`} title={space.label} onClick={onToggle}>
          {space.label.charAt(0)}
        </button>
      ) : (
        <>
          <SpaceSwitcher variant="sidebar" />
          <GlobalSearch />
        </>
      )}

      <div className="sh-sb__services">
        {rail ? null : <div className="sh-eyebrow">Services</div>}
        <nav className="sh-sb__nav" aria-label="Services">
          <NavItem service={HOME} pathname={pathname} rail={rail} />
          {PRIMARY_SERVICES.map((s) => (
            <NavItem key={s.id} service={s} pathname={pathname} rail={rail} />
          ))}
          <AddItem rail={rail} />
        </nav>
      </div>

      <div className="sh-sb__foot" data-rail={rail}>
        {rail ? null : (
          <>
            <Avatar initial={user.initial} size={32} tone="surface" />
            <span className="sh-sb__user">{user.name}</span>
          </>
        )}
        <ThemeToggle size={rail ? 48 : 44} />
        {rail ? <Avatar initial={user.initial} size={40} tone="accent" label={user.name} /> : null}
      </div>
    </aside>
  );
}
